// La résidence principale : les biens qu'on peut acheter, le crédit immobilier et ses frais, la courbe des prix.
// Des ordres de grandeur de 2026 pour une ville moyenne ; aucun bien ni prix réel.
import type { TextKey } from './fr';
import type { PropertyKind } from '../engine/state';

export interface PropertyDef {
  title: TextKey;
  desc: TextKey;
  /** Le prix au début de la partie ; il suit ensuite la courbe de l'immobilier. */
  price: number;
  /** Par mois, aux prix du début de la partie : la copropriété, l'eau et l'entretien, l'énergie, l'assurance. */
  copropriete: number;
  charges: number;
  energy: number;
  insurance: number;
  /** La taxe foncière de l'année, prélevée chaque mois. */
  propertyTax: number;
  /** Ce que le logement apporte à la cible du bien-être (le studio en location : 10). */
  wellbeing: number;
}

export const PROPERTIES: Record<PropertyKind, PropertyDef> = {
  t2: {
    title: 'property.t2.title',
    desc: 'property.t2.desc',
    price: 150000,
    copropriete: 110,
    charges: 0,
    energy: 60,
    insurance: 15,
    propertyTax: 850,
    wellbeing: 12,
  },
  t3: {
    title: 'property.t3.title',
    desc: 'property.t3.desc',
    price: 210000,
    copropriete: 160,
    charges: 0,
    energy: 85,
    insurance: 20,
    propertyTax: 1200,
    wellbeing: 15,
  },
  maison: {
    title: 'property.maison.title',
    desc: 'property.maison.desc',
    price: 275000,
    copropriete: 0,
    charges: 45,
    energy: 130,
    insurance: 30,
    propertyTax: 1500,
    wellbeing: 18,
  },
};

export const PROPERTY_KINDS: PropertyKind[] = ['t2', 't3', 'maison'];

export type MortgageYears = 15 | 20 | 25;

/** Le crédit immobilier et ses frais. */
export const MORTGAGE = {
  /** Le taux selon la durée, en années. */
  rates: { 15: 0.031, 20: 0.0325, 25: 0.034 } as Record<MortgageYears, number>,
  durations: [15, 20, 25] as MortgageYears[],
  /** Les frais de notaire dans l'ancien, en part du prix. */
  notaryRate: 0.075,
  /** La garantie du prêt, en part de la somme empruntée. */
  guaranteeRate: 0.01,
  /** Les frais de dossier de la banque, aux prix du début de la partie. */
  fileFee: 1000,
  /** L'assurance emprunteur par an, en part de la somme empruntée : celle de la banque, ou une assurance déléguée. */
  insurance: { banque: 0.003, deleguee: 0.0012 },
  /** Les mensualités de tous les crédits, assurance comprise, ne doivent pas dépasser cette part du salaire. */
  maxDebtRatio: 0.35,
  /** Un remboursement anticipé partiel : au moins cette part de la somme empruntée. */
  prepayMinShare: 0.1,
  /** Les pénalités : le moins cher entre six mois d'intérêts sur la somme remboursée et 3 % du capital restant dû. */
  penaltyMonths: 6,
  penaltyCap: 0.03,
  /** À la revente, les frais d'agence. */
  agencyRate: 0.05,
  /** Le projet « Un toit à moi » : le crédit remboursé avant cet âge. */
  projectAge: 55,
};

/**
 * La courbe des prix de l'immobilier : +3,5 % par an en temps normal, et de temps en temps (tous les vingt ans
 * environ) trois à cinq années de baisse, à −4 % par an. Sur une vie, environ +2,5 % par an.
 */
export const REAL_ESTATE = {
  growth: 0.035,
  slump: -0.04,
  vol: 0.015,
  /** La chance par mois qu'une baisse commence, et sa durée en mois. */
  slumpChance: 1 / 264,
  slumpMonths: [36, 60] as [number, number],
};
